import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  unlink,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from './config';
import { createUserProfile, updateUserProfile, getUserProfile } from './firestore';
import {
  confirmPhoneVerification as confirmPhoneVerificationAPI,
  clearPhoneVerification as clearPhoneVerificationAPI,
  sendCustomVerificationEmail,
} from '../lib/api';

/**
 * Sign up a new user with email and password
 * Creates Firebase Auth account and Firestore user profile
 */
export async function signUp(email, password, displayName) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Update display name in Firebase Auth
  await updateProfile(user, { displayName });

  // Create user profile in Firestore
  console.log('Creating Firestore user profile for:', user.uid);
  try {
    await createUserProfile(user.uid, {
    email,
    displayName,
    photoURL: null,
    phone: null,
    phoneVerified: false,
    locationArea: null,
    stripeConnectAccountId: null,
    stripeConnectStatus: null,
    stripeConnectOnboardedAt: null,
    rating: 0,
    reviewCount: 0,
    stats: {
      rafflesCreated: 0,
      rafflesWon: 0,
      rafflesEntered: 0,
    },
  });
    console.log('Firestore user profile created successfully');
  } catch (firestoreError) {
    console.error('Failed to create Firestore user profile:', firestoreError);
    throw firestoreError;
  }

  // Send custom branded email verification via SendGrid
  try {
    await sendCustomVerificationEmail();
  } catch (emailErr) {
    // Don't fail signup if email fails - user can resend from verify page
    console.error('Failed to send verification email:', emailErr);
  }

  return user;
}

/**
 * Sign in existing user with email and password
 */
export async function signIn(email, password) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

/**
 * Sign in with Google
 * Creates Firestore profile if this is a new user
 */
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: 'select_account',
  });

  const result = await signInWithPopup(auth, provider);
  const user = result.user;

  // Check if this is a new user (no Firestore profile yet)
  const existingProfile = await getUserProfile(user.uid);

  if (!existingProfile) {
    // Create user profile in Firestore for new Google users
    console.log('Creating Firestore user profile for new Google user:', user.uid);
    await createUserProfile(user.uid, {
      email: user.email,
      displayName: user.displayName || user.email?.split('@')[0] || 'User',
      photoURL: user.photoURL || null,
      phone: null,
      phoneVerified: false,
      locationArea: null,
      stripeConnectAccountId: null,
      stripeConnectStatus: null,
      stripeConnectOnboardedAt: null,
      rating: 0,
      reviewCount: 0,
      stats: {
        rafflesCreated: 0,
        rafflesWon: 0,
        rafflesEntered: 0,
      },
    });
    console.log('Firestore user profile created for Google user');
  }

  return user;
}

/**
 * Sign out current user
 */
export async function signOut() {
  await firebaseSignOut(auth);
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Resend email verification to current user (uses custom SendGrid template)
 */
export async function resendVerificationEmail() {
  if (auth.currentUser) {
    await sendCustomVerificationEmail();
  }
}

/**
 * Update user's display name
 */
export async function updateDisplayName(displayName) {
  if (!auth.currentUser) throw new Error('No authenticated user');

  await updateProfile(auth.currentUser, { displayName });
  await updateUserProfile(auth.currentUser.uid, { displayName });
}

/**
 * Update user's profile photo URL
 */
export async function updatePhotoURL(photoURL) {
  if (!auth.currentUser) throw new Error('No authenticated user');

  await updateProfile(auth.currentUser, { photoURL });
  await updateUserProfile(auth.currentUser.uid, { photoURL });
}

/**
 * Update user's email (requires recent login)
 */
export async function changeEmail(newEmail, currentPassword) {
  if (!auth.currentUser) throw new Error('No authenticated user');

  // Re-authenticate first
  const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
  await reauthenticateWithCredential(auth.currentUser, credential);

  // Update email
  await updateEmail(auth.currentUser, newEmail);
  await updateUserProfile(auth.currentUser.uid, { email: newEmail });

  // Send custom branded verification to new email
  try {
    await sendCustomVerificationEmail();
  } catch (emailErr) {
    console.error('Failed to send verification email:', emailErr);
  }
}

/**
 * Update user's password (requires recent login)
 */
export async function changePassword(currentPassword, newPassword) {
  if (!auth.currentUser) throw new Error('No authenticated user');

  // Re-authenticate first
  const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
  await reauthenticateWithCredential(auth.currentUser, credential);

  // Update password
  await updatePassword(auth.currentUser, newPassword);
}

// ============================================
// PHONE VERIFICATION (OTP)
// ============================================

let recaptchaVerifier = null;
let confirmationResult = null;

/**
 * Check if user already has phone provider linked
 */
export function hasPhoneLinked() {
  if (!auth.currentUser) return false;
  return auth.currentUser.providerData.some(
    (provider) => provider.providerId === 'phone'
  );
}

/**
 * Get linked phone number from Firebase Auth
 */
export function getLinkedPhone() {
  if (!auth.currentUser) return null;
  const phoneProvider = auth.currentUser.providerData.find(
    (provider) => provider.providerId === 'phone'
  );
  return phoneProvider?.phoneNumber || auth.currentUser.phoneNumber || null;
}

/**
 * Sync phone from Firebase Auth to Firestore
 * Used when Auth has phone but Firestore doesn't
 * Uses Cloud Function to securely validate and update
 */
export async function syncPhoneToFirestore() {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  const linkedPhone = getLinkedPhone();
  if (!linkedPhone) {
    throw new Error('No phone linked to account');
  }

  // Use Cloud Function to securely sync phone to Firestore
  // This validates that the phone is actually linked in Auth
  const result = await confirmPhoneVerificationAPI();
  return result.phoneNumber;
}

/**
 * Unlink phone from account (to allow re-verification with different number)
 */
export async function unlinkPhone() {
  if (!auth.currentUser) {
    throw new Error('No authenticated user');
  }

  // Unlink from Firebase Auth if linked
  if (hasPhoneLinked()) {
    console.log('[Phone Auth] Unlinking phone from Firebase Auth');
    await unlink(auth.currentUser, 'phone');
    console.log('[Phone Auth] Phone unlinked from Firebase Auth');
  }

  // Clear from Firestore via Cloud Function
  // This validates that phone is actually unlinked from Auth before clearing Firestore
  console.log('[Phone Auth] Clearing phone from Firestore via Cloud Function');
  await clearPhoneVerificationAPI();
  console.log('[Phone Auth] Phone cleared from Firestore');
}

/**
 * Initialize reCAPTCHA verifier for phone auth
 * Call this once when the phone verification UI mounts
 */
export async function initRecaptchaVerifier(containerId, onReady) {
  // Clear any existing verifier
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      // Ignore clear errors
    }
    recaptchaVerifier = null;
  }

  // Check if container exists
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(`reCAPTCHA container '${containerId}' not found in DOM`);
  }

  // Clear container content (in case of re-initialization)
  container.innerHTML = '';

  recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {
      // reCAPTCHA solved - fires when challenge is completed
      console.log('[Phone Auth] reCAPTCHA solved');
    },
    'expired-callback': () => {
      // Response expired, reset reCAPTCHA
      console.log('[Phone Auth] reCAPTCHA expired');
    },
  });

  // Render the reCAPTCHA widget and call onReady when done
  try {
    await recaptchaVerifier.render();
    console.log('[Phone Auth] reCAPTCHA rendered successfully');
    onReady?.();
  } catch (err) {
    console.error('[Phone Auth] reCAPTCHA render error:', err);
    // Reset verifier on error
    recaptchaVerifier = null;
    throw err;
  }

  return recaptchaVerifier;
}

/**
 * Send OTP to phone number
 * Phone number must be in E.164 format (e.g., +353851234567)
 */
export async function sendPhoneOTP(phoneNumber) {
  if (!recaptchaVerifier) {
    throw new Error('reCAPTCHA not initialized. Call initRecaptchaVerifier first.');
  }

  console.log('[Phone Auth] Sending OTP to:', phoneNumber);

  try {
    confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    console.log('[Phone Auth] OTP sent successfully');
    return confirmationResult;
  } catch (err) {
    console.error('[Phone Auth] Failed to send OTP:', err.code, err.message);
    // Reset reCAPTCHA on error so it can be re-rendered
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {
        // Ignore
      }
      recaptchaVerifier = null;
    }
    throw err;
  }
}

/**
 * Verify OTP code and link phone to existing account
 */
export async function verifyPhoneOTP(code) {
  if (!confirmationResult) {
    throw new Error('No confirmation result. Call sendPhoneOTP first.');
  }

  if (!auth.currentUser) {
    throw new Error('No authenticated user. User must be logged in to verify phone.');
  }

  console.log('[Phone Auth] Verifying OTP code');

  // Get credential from OTP
  const credential = PhoneAuthProvider.credential(
    confirmationResult.verificationId,
    code
  );

  try {
    // Link phone credential to existing account
    await linkWithCredential(auth.currentUser, credential);
    console.log('[Phone Auth] Phone linked successfully');
  } catch (err) {
    // Handle "already linked" error - phone might be linked but Firestore not updated
    if (err.code === 'auth/provider-already-linked') {
      console.log('[Phone Auth] Phone already linked, syncing to Firestore via Cloud Function');
      try {
        const result = await confirmPhoneVerificationAPI();
        confirmationResult = null;
        return result.phoneNumber;
      } catch (syncErr) {
        console.error('[Phone Auth] Cloud Function sync failed:', syncErr);
        throw syncErr;
      }
    }
    throw err;
  }

  // Update Firestore via Cloud Function (validates phone is actually linked)
  const phoneNumber = auth.currentUser.phoneNumber || getLinkedPhone();
  console.log('[Phone Auth] Confirming via Cloud Function, phone:', phoneNumber);

  try {
    const result = await confirmPhoneVerificationAPI();
    console.log('[Phone Auth] Cloud Function confirmed phone:', result.phoneNumber);
  } catch (apiErr) {
    // Phone linked but Cloud Function failed
    console.error('[Phone Auth] Cloud Function failed:', apiErr);
    throw new Error('Phone verified but profile update failed. Please try again.');
  }

  // Clear confirmation result
  confirmationResult = null;

  return phoneNumber;
}

/**
 * Clean up reCAPTCHA verifier
 */
export function cleanupRecaptcha() {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      // Ignore clear errors
    }
    recaptchaVerifier = null;
  }
  confirmationResult = null;
}

// ============================================
// AUTH STATE
// ============================================

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get current user
 */
export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Check if user's email is verified
 */
export function isEmailVerified() {
  return auth.currentUser?.emailVerified ?? false;
}

/**
 * Reload current user to get fresh data (e.g., after email verification)
 */
export async function reloadUser() {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser;
  }
  return null;
}
